<template>
    <div class="product-gallery">
        <v-carousel :show-arrows="nextCarouselItems.length > 0" hide-delimiters height="auto">
            <v-carousel-item key="0">
                <v-row no-gutters>
                    <v-col class="d-flex child-flex pe-2 ps-0" cols="4">
                        <v-card flat rounded="0" class="d-flex full-width">
                            <v-img
                                v-if="mainImage && mainImage.url"
                                :src="$filters.imageUrl(mainImage.url, 600)"
                                :title="imgTitle(mainImage)"
                                :lazy-src="$filters.srcHost('/placeholder.png')"
                                aspect-ratio="1"
                                cover
                                class="bg-grey-lighten-2 cursor-pointer"
                                @click="showPhotoSwipe(0)"
                                @error="hidePlaceholder(mainImage.url)"
                            >
                                <template v-slot:placeholder>
                                    <v-row
                                        v-show="imagePlaceholdersVision[0]"
                                        class="fill-height ma-0"
                                        align="center"
                                        justify="center"
                                    >
                                        <v-progress-circular indeterminate color="grey-lighten-5" />
                                    </v-row>
                                </template>
                            </v-img>
                        </v-card>
                    </v-col>
                    <v-col cols="8">
                        <v-row dense>
                            <v-col
                                v-for="(image, imageIndex) in firstCarouselItem"
                                :key="imageIndex"
                                class="d-flex child-flex"
                                cols="3"
                            >
                                <v-card flat rounded="0" class="d-flex full-width">
                                    <v-img
                                        v-if="image && image.url"
                                        :src="$filters.imageUrl(image.url, 350)"
                                        :title="imgTitle(image)"
                                        :lazy-src="$filters.srcHost('/placeholder.png')"
                                        aspect-ratio="1"
                                        cover
                                        class="bg-grey-lighten-2 cursor-pointer"
                                        @click="showPhotoSwipe(imageIndex + 1)"
                                        @error="hidePlaceholder(image.url)"
                                    >
                                        <template v-slot:placeholder>
                                            <v-row
                                                v-show="imagePlaceholdersVision[getImageIndex(image.url)]"
                                                class="fill-height ma-0"
                                                align="center"
                                                justify="center"
                                            >
                                                <v-progress-circular indeterminate color="grey-lighten-5" />
                                            </v-row>
                                        </template>
                                    </v-img>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-carousel-item>
            <v-carousel-item v-for="(images, index) in nextCarouselItems" :key="index + 1">
                <v-row dense>
                    <v-col v-for="(image, imageIndex) in images" :key="imageIndex" class="d-flex child-flex" cols="2">
                        <v-card flat rounded="0" class="d-flex full-width">
                            <v-img
                                v-if="image && image.url"
                                :src="$filters.imageUrl(image.url, 600)"
                                :title="imgTitle(image)"
                                :lazy-src="$filters.srcHost('/placeholder.png')"
                                aspect-ratio="1"
                                cover
                                class="bg-grey-lighten-2"
                                @error="hidePlaceholder(image.url)"
                                @click="
                                    showPhotoSwipe(
                                        imagesCountInFirstCarouselItem + index * imagesCountInCarouselItem + imageIndex
                                    )
                                "
                            >
                                <template v-slot:placeholder>
                                    <v-row
                                        v-show="imagePlaceholdersVision[getImageIndex(image.url)]"
                                        class="fill-height ma-0"
                                        align="center"
                                        justify="center"
                                    >
                                        <v-progress-circular indeterminate color="grey-lighten-5" />
                                    </v-row>
                                </template>
                            </v-img>
                        </v-card>
                    </v-col>
                </v-row>
            </v-carousel-item>
        </v-carousel>
        <v-photo-swipe
            v-if="photoSwipeItems?.length"
            ref="photoSwipe"
            :items="photoSwipeItems"
            :photo-swipe-options="photoSwipeOptions"
        />
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import VPhotoSwipe from '@/components/snippets/VPhotoSwipe.vue'

    @Component({components: {VPhotoSwipe}})
    export class ProductGallery extends Vue {
        @Prop({default: () => []}) imagesData

        photoSwipeOptions = {
            index: 0,
        }
        imagePlaceholdersVision = []

        mounted() {
            this.mapImagePlaceholders()
        }

        showPhotoSwipe(index) {
            this.photoSwipeOptions.index = index
            this.$refs.photoSwipe.openLightbox(index)
        }

        mapImagePlaceholders() {
            this.imagePlaceholdersVision[0] = true
            this.images.forEach((image, index) => {
                this.imagePlaceholdersVision[index + 1] = true
            })
        }

        getImageIndex(url) {
            return this.images.findIndex(image => image.url === url) + 1
        }

        hidePlaceholder(url) {
            this.imagePlaceholdersVision[this.getImageIndex(url)] = false
        }

        imgTitle(img) {
            return img.title === 'null' ? null : img.title
        }

        get imagesCountInFirstCarouselItem() {
            return 9
        }

        get firstCarouselItem() {
            return this.images.slice(1, this.imagesCountInFirstCarouselItem).filter(Boolean)
        }

        get imagesCountInCarouselItem() {
            return 12
        }

        get nextCarouselItems() {
            const nextCarouselItems = []
            for (
                let i = this.imagesCountInFirstCarouselItem;
                i < this.images.length;
                i = i + this.imagesCountInCarouselItem
            ) {
                nextCarouselItems.push(this.images.slice(i, i + this.imagesCountInCarouselItem).filter(Boolean))
            }
            return nextCarouselItems
        }

        get mainImage() {
            return this.images[0]
        }

        get images() {
            return [...this.imagesData].sort(a => (a.mainImage ? -1 : 0))
        }

        get photoSwipeItems() {
            return this.images.map(({url, title}) => ({
                src: this.$filters.imageUrl(url, 800),
                w: 0,
                h: 0,
                title: title === 'null' ? null : title,
            }))
        }
    }

    export default ProductGallery
</script>
